import React, { ReactElement, Fragment, useState, useEffect, createRef } from "react";
import { Translate } from "react-redux-i18n";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  Typography,
  Box,
} from "@mui/material";
import { CartesianProduct } from "js-combinatorics";
import {
  calculateOddBonus, calculateMultiplicationOfOddCombPlayedCoupon,
  calculatePotentialWinArrayPlayedCoupon
} from "redux/coupon/coupon.utils";
import FontAwesomeIcon from "components/shared/FontAwesomeIcon";
import styles from './styles.module.scss'
import { handleScroll, convertOddValue } from "services/utils";
import { CombinationOdd, PlayedCouponOddContent, saveCombinationFilter } from "redux/coupon/couponSlice";
import { saveBettingWalletId } from "redux/wallet/walletSlice";

export default function IntegralCombinations(): ReactElement {
  const dispatch = useAppDispatch();
  const playedCoupon = useAppSelector(state => state.coupon.playedCoupon);
  const bonus = useAppSelector(state => state.coupon.bettingConfig?.bonus);
  const rankBonus = useAppSelector(state => state.coupon.bettingConfig?.setting?.rank_bonus);
  const limit = useAppSelector(state => state.coupon.bettingConfig?.setting["win_integral"]);
  const currency = useAppSelector(state => state.auth.authWallet?.currency.symbol);
  const combinationsFilter = useAppSelector(state => state.coupon.combinationFilter);
  const walletId = useAppSelector(state => state.auth.authWallet?.id)
  const playedCouponWalletId = useAppSelector(state => state.wallet.playedCouponWalletId)
  const divider = useAppSelector(state => state.auth.authWallet?.currency.divider)
  const decimal = useAppSelector(state => state.auth.authWallet?.currency.decimal)


  const [oddCombinations, setCombinations] = useState<CombinationOdd[][]>()
  const [oddCombinationsLength, setOddCombinationsLength] = useState<number>()
  const [itemsToLoad, setItemsToLoad] = useState<number>(20)

  const ref: any = createRef();
  const roundedAmount = playedCoupon && oddCombinationsLength &&
    (playedCoupon.bet_amount / oddCombinationsLength)
      .toFixed(4)
  const composeCouponData = (
    data: PlayedCouponOddContent[]
  ): Record<string, CombinationOdd[]> => {
    const obj: Record<string, CombinationOdd[]> = {};
    data
      .map((odd: PlayedCouponOddContent) => {
        const newOdd = {
          id: odd.odd.id,
          eventId: odd.event.id,
          marketId: odd.market.id,
          oddLabel: odd.odd.label,
          spreadLabel: odd.odd.spread,
          marketLabel: odd.market.label,
          unique: odd.unique,
          value: odd.odd.void ? 1 : odd.odd.value,
          code: odd.code,
          fix: odd.fix,
          eventLabel: odd.event.label,
          locked: odd.odd.locked,
          tournamentId: odd.tournament.id,
          running: odd.odd.running,
          void: odd.odd.void,
          winner: odd.odd.winner,

        };
        if (obj[odd.event.id]) {
          obj[odd.event.id].push(newOdd);
        } else {
          obj[odd.event.id] = [newOdd];
        }
      });
    return obj;
  };

  const handleDesktopScroll = (
    e: React.SyntheticEvent,
    setGamesToShow: React.Dispatch<number>,
    gamesToShow: number,
    gamesToAdd: number
  ) => {
    handleScroll(e, setGamesToShow, gamesToShow, gamesToAdd)
  }
  useEffect(() => {
    if (playedCoupon) {
      const composedOdds = composeCouponData(playedCoupon.odds);
      const cartesianProductOfOdds = [
        ...new CartesianProduct(...Object.values(composedOdds)),
      ]
      setOddCombinationsLength(cartesianProductOfOdds.length)
      switch (combinationsFilter) {
        case "all":
          setCombinations(cartesianProductOfOdds)
          break;
        case "winner":
          setCombinations(cartesianProductOfOdds.filter((item: CombinationOdd[]) => item.every((odd: CombinationOdd) => (odd.winner === true || odd.void === true))))
          break;
        case "running":
          setCombinations(cartesianProductOfOdds.filter((combo: CombinationOdd[]) =>
            !combo.find((odd: CombinationOdd) => (odd.running === false && odd.winner === false && odd.void === false)) &&
            !combo.every((odd: CombinationOdd) => (odd.winner === true || odd.void === true))
          ))
          break;
        case "loser":
          setCombinations(cartesianProductOfOdds.filter((item: CombinationOdd[]) => item.find((odd: CombinationOdd) => odd.winner === false && odd.running === false && odd.void === false)))
          break;
        default:
          setCombinations(cartesianProductOfOdds)
          break;
      }
    }
  }, [playedCoupon, combinationsFilter])

  useEffect(() => {
    dispatch(saveCombinationFilter("all"))
    if (walletId && playedCouponWalletId && walletId !== playedCouponWalletId) {
      dispatch(saveBettingWalletId(playedCouponWalletId))
    } else if (walletId && playedCouponWalletId && walletId === playedCouponWalletId) {
      dispatch(saveBettingWalletId(walletId))
    }
    return () => {
      walletId && dispatch(saveBettingWalletId(walletId))
    }
  }, [])

  return (
    <Box
      ref={ref}
      onScroll={(e) => handleDesktopScroll(e, setItemsToLoad, itemsToLoad, 20)}
      className={styles.container}
    >
      {oddCombinations && oddCombinations?.length <= 0 &&
        <Typography className={styles.noCombinationsMessage}><Translate value="couponDetail.noCombinationsFound" /></Typography>
      }
      <Box className={styles.gridContainer}>
        {
          oddCombinations?.slice(0, itemsToLoad).map((comb: CombinationOdd[], index: number) => {
            const calcBonus = calculateOddBonus(
              [comb],
              bonus,
              rankBonus);
            const prodOfOddCombinations = calculateMultiplicationOfOddCombPlayedCoupon(
              [comb]
            )
            const calculatedPotWinArray = calculatePotentialWinArrayPlayedCoupon(
              Number(roundedAmount),
              prodOfOddCombinations,
            );
            return <Box key={index} className={styles.combinationContainer}>
              <Box className={styles.combinationHeader}>
                <Typography className={styles.headerLabel} sx={{ width: "40%" }}><Translate value="couponDetail.combinationsEvent" /></Typography>
                <Typography className={styles.headerLabel} sx={{ width: "30%" }}><Translate value="couponDetail.combinationsMarket" /></Typography>
                <Typography className={styles.headerLabel} sx={{ width: "15%" }}><Translate value="couponDetail.combinationsOdd" /></Typography>
                <Typography className={styles.headerLabel} sx={{ width: "15%" }}><Translate value="couponDetail.combinationsValue" /></Typography>
              </Box>
              {
                comb.map((el: CombinationOdd) => {
                  return <Box key={el.unique} className={styles.combinationContent}>
                    <Typography className={styles.contentLabel} sx={{ width: "40%" }}>{el.eventLabel}</Typography>
                    <Typography className={styles.contentLabel} sx={{ width: "30%" }}>{el.marketLabel}</Typography>
                    <Typography className={styles.contentLabel} sx={{ width: "15%", textAlign: "center" }}>{el.oddLabel}</Typography>
                    <>
                      <Typography className={styles.contentLabel} sx={{ width: "15%", textAlign: "center" }}>{el.value && convertOddValue(el.value)}</Typography>
                      <Box className={
                        //when its running = yellow , void = white , winner green , else red
                        `
                            ${styles.state}
                            ${el.running
                          ? styles.stateYellow
                          : el.winner
                            ? styles.stateGreen
                            : el.void
                              ? styles.stateWhite
                              : styles.stateRed}`
                      }
                      >
                        <FontAwesomeIcon type="fas" name="circle" />
                      </Box>
                    </>

                  </Box>
                })
              }
              <Box className={styles.calcContainer}>
                <Box className={styles.calcContainerFirstItem}>
                  <Typography className={styles.totalLabel} color="white"><Translate value="couponDetail.combinationsAmountCol" /></Typography>
                  <Typography className={styles.totalLabel} >{currency} {roundedAmount && divider && (Number(roundedAmount) / divider).toFixed(decimal)}</Typography>
                </Box>
                <Box className={styles.calcContainerItem}>
                  <Typography className={styles.totalLabel} ><Translate value="couponDetail.combinationsWin" /></Typography>
                  <Typography className={styles.totalLabel} >{currency} {divider && (limit && (calculatedPotWinArray[0] > limit) ? (limit / divider).toFixed(decimal) : (calculatedPotWinArray[0] / divider).toFixed(decimal))}</Typography>
                </Box>
                <Box className={styles.calcContainerItem}>
                  <Typography className={styles.totalLabel} ><Translate value="couponDetail.combinationsBonus" /></Typography>
                  <Typography className={styles.totalLabel} >{currency} {divider && (limit && ((calcBonus[0] * calculatedPotWinArray[0]) / 100 > limit) ? (limit / divider).toFixed(decimal) : (((calcBonus[0] * calculatedPotWinArray[0]) / 100) / divider).toFixed(decimal))}</Typography>
                </Box>

                <Box className={styles.calcContainerLastItem}>
                  <Typography className={styles.totalLabel} ><Translate value="couponDetail.combinationsTotalWin" /></Typography>
                  <Typography className={styles.totalLabel} >{currency} {divider && (limit && ((calculatedPotWinArray[0] + (calcBonus[0] * calculatedPotWinArray[0]) / 100) > limit) ? (limit / divider).toFixed(decimal) : ((calculatedPotWinArray[0] + (calcBonus[0] * calculatedPotWinArray[0]) / 100) / divider).toFixed(decimal))}</Typography>
                </Box>
              </Box>
            </Box>
          })
        }
      </Box>

    </Box >
  )
}